import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface Bay {
  id: string;
  bay_number: string;
  bayNumber?: string;
  number?: string;
  order_id: string | null;
  orderId?: string | null;
  order_number: string | null;
  orderNumber?: string | null;
  sidemark: string | null;
  dealer_company: string | null;
  dealerCompany?: string | null;
  dealer_name: string | null;
  dealerName?: string | null;
  assigned_at: string | null;
}

export default function BayManagementPage() {
  const [bays, setBays] = useState<Bay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBay, setSelectedBay] = useState<string>('');
  const [orderSearch, setOrderSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [releasing, setReleasing] = useState<string | null>(null);

  const loadBays = useCallback(async () => {
    try {
      const data = await api.getBays();
      setBays(data);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load bays');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBays();
    const interval = setInterval(loadBays, 15000);
    return () => clearInterval(interval);
  }, [loadBays]);

  const getBayNumber = (bay: Bay) => bay.bay_number || bay.bayNumber || bay.number || '';
  const getOrderId = (bay: Bay) => bay.order_id || bay.orderId || null;
  const getOrderNumber = (bay: Bay) => bay.order_number || bay.orderNumber || '';
  const getDealerCompany = (bay: Bay) => bay.dealer_company || bay.dealerCompany || bay.dealer_name || bay.dealerName || '';

  const handleOpenAssign = (bayNumber: string) => {
    setSelectedBay(bayNumber);
    setOrderSearch('');
    setSearchResults([]);
    setShowAssignModal(true);
  };

  const handleSearchOrder = async () => {
    if (!orderSearch.trim()) return;
    try {
      setSearching(true);
      // Use the lookupOrder API to search for an order
      const result = await api.lookupOrder(orderSearch.trim());
      if (result) {
        setSearchResults(Array.isArray(result) ? result : [result]);
      } else {
        setSearchResults([]);
      }
    } catch (err: any) {
      // If search fails, show empty results
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async (orderId: string) => {
    try {
      setAssigning(true);
      await api.assignBay(selectedBay, orderId);
      setShowAssignModal(false);
      loadBays();
    } catch (err: any) {
      alert(err.message || 'Failed to assign bay');
    } finally {
      setAssigning(false);
    }
  };

  const handleRelease = async (bayNumber: string) => {
    if (!confirm(`Release bay ${bayNumber}?`)) return;
    try {
      setReleasing(bayNumber);
      await api.releaseBay(bayNumber);
      loadBays();
    } catch (err: any) {
      alert(err.message || 'Failed to release bay');
    } finally {
      setReleasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Shipping Bays</h1>
          <p className="text-gray-400 mt-1">
            {bays.filter(b => getOrderId(b)).length} of {bays.length} bays occupied
          </p>
        </div>
        <button
          onClick={loadBays}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
          {error}
        </div>
      )}

      {bays.length === 0 && !error ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Bays Configured</h3>
          <p className="text-gray-400">Shipping bays have not been set up yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {bays.map((bay) => {
            const bayNum = getBayNumber(bay);
            const orderId = getOrderId(bay);
            const isOccupied = !!orderId;
            const isReleasing = releasing === bayNum;

            return (
              <div
                key={bay.id || bayNum}
                className={`rounded-xl border-2 p-5 transition-all ${
                  isOccupied
                    ? 'border-blue-600 bg-gray-800'
                    : 'border-green-600 bg-gray-800 hover:border-green-400 cursor-pointer'
                }`}
                onClick={() => !isOccupied && handleOpenAssign(bayNum)}
              >
                {/* Bay Number */}
                <div className="text-center mb-4">
                  <div className={`text-2xl font-bold ${isOccupied ? 'text-blue-400' : 'text-green-400'}`}>
                    {bayNum.startsWith('BAY') ? bayNum : `BAY-${bayNum}`}
                  </div>
                </div>

                {isOccupied ? (
                  <div className="space-y-3">
                    <div className="text-center">
                      <div className="text-white font-semibold text-lg">
                        {getOrderNumber(bay)}
                      </div>
                      {bay.sidemark && (
                        <div className="text-gray-400 text-sm">{bay.sidemark}</div>
                      )}
                      {getDealerCompany(bay) && (
                        <div className="text-gray-500 text-xs mt-1">{getDealerCompany(bay)}</div>
                      )}
                    </div>
                    {bay.assigned_at && (
                      <div className="text-center text-gray-500 text-xs">
                        Since {new Date(bay.assigned_at).toLocaleDateString()}
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRelease(bayNum); }}
                      disabled={isReleasing}
                      className="w-full px-3 py-2 bg-red-600/20 text-red-400 border border-red-700 rounded-lg hover:bg-red-600/40 disabled:opacity-50 transition-colors text-sm font-medium"
                    >
                      {isReleasing ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-400"></div>
                          Releasing...
                        </span>
                      ) : (
                        'Release Bay'
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-green-400 font-semibold text-lg mb-2">EMPTY</div>
                    <p className="text-gray-500 text-sm">Click to assign an order</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Assign Bay Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-2">
              Assign Order to {selectedBay.startsWith('BAY') ? selectedBay : `BAY-${selectedBay}`}
            </h3>
            <p className="text-gray-400 text-sm mb-4">Search for an order by order number.</p>

            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchOrder()}
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter order number..."
                autoFocus
              />
              <button
                onClick={handleSearchOrder}
                disabled={searching || !orderSearch.trim()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 transition-colors"
              >
                {searching ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Search'
                )}
              </button>
            </div>

            {/* Search results */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {searchResults.length > 0 ? (
                searchResults.map((order: any) => (
                  <button
                    key={order.id}
                    onClick={() => handleAssign(order.id)}
                    disabled={assigning}
                    className="w-full p-3 bg-gray-700 rounded-lg text-left hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium">
                          {order.order_number || order.orderNumber}
                        </div>
                        <div className="text-gray-400 text-sm">
                          {order.sidemark && <span>{order.sidemark} - </span>}
                          {order.dealer_company || order.dealerCompany || ''}
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))
              ) : orderSearch && !searching ? (
                <p className="text-gray-500 text-sm text-center py-4">
                  No orders found. Try a different search term.
                </p>
              ) : null}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
